import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Box,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Grid,
  Tooltip,
} from '@mui/material';
import { leaveService } from '../../services/leaveService';
import { projectService } from '../../services/projectService';
import { employeeService } from '../../services/employeeService';
import { format } from 'date-fns';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ConfirmDialog from '../Common/ConfirmDialog';
import * as XLSX from 'xlsx-js-style';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Table header styles
const headerCellStyle = {
  backgroundColor: '#00897B',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '0.85rem',
  borderBottom: '3px solid #00695C',
  textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
};

const LeaveList = () => {
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [filteredLeaves, setFilteredLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');

  // Edit Dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [editLeaveCount, setEditLeaveCount] = useState(0);
  const [editError, setEditError] = useState('');

  // Add Employee Dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [newLeaveCount, setNewLeaveCount] = useState(0);
  const [addError, setAddError] = useState('');

  // Delete Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leaveToDelete, setLeaveToDelete] = useState(null);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      loadEmployeesByProject();
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedProject && selectedMonth) {
      loadLeaves();
    } else {
      setFilteredLeaves([]);
    }
  }, [selectedProject, selectedMonth]);

  const loadProjects = async () => {
    try {
      const data = await projectService.getAll();
      setProjects(data);
    } catch (err) {
      setError('Failed to load projects');
    }
  };

  const loadEmployeesByProject = async () => {
    try {
      if (!selectedProject) {
        setEmployees([]);
        return;
      }
      const data = await employeeService.getByProject(selectedProject);
      setEmployees(data);
    } catch (err) {
      setError('Failed to load employees for selected project');
    }
  };

  const loadLeaves = async () => {
    try {
      setLoading(true);
      setError('');

      // Get all leaves and filter by project and month
      const allLeaves = await leaveService.getAll();

      // Format selected month as YYYY-MM-01 for comparison
      const monthDate = format(new Date(selectedMonth + '-01'), 'yyyy-MM-dd');

      const filtered = allLeaves.filter(leave =>
        leave.project_code === selectedProject &&
        leave.month === monthDate
      );

      setFilteredLeaves(filtered);
      setLeaves(allLeaves);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load leaves');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (leave) => {
    setEditingLeave(leave);
    setEditLeaveCount(leave.leave_count);
    setEditError('');
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    try {
      setEditError('');

      // Format month as YYYY-MM-01 for API
      const monthDate = format(new Date(selectedMonth + '-01'), 'yyyy-MM-dd');

      await leaveService.create({
        emp_id: editingLeave.employee_id,  // Use UUID for API
        project_code: editingLeave.project_code,
        month: monthDate,
        leave_count: parseInt(editLeaveCount),
      });

      setSuccess('Leave updated successfully');
      setEditDialogOpen(false);
      loadLeaves();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update leave');
    }
  };

  const handleAddEmployee = () => {
    setSelectedEmployee(null);
    setNewLeaveCount(0);
    setAddError('');
    setAddDialogOpen(true);
  };

  const handleAddSave = async () => {
    try {
      setAddError('');

      if (!selectedEmployee) {
        setAddError('Please select an employee');
        return;
      }

      // Format month as YYYY-MM-01 for API
      const monthDate = format(new Date(selectedMonth + '-01'), 'yyyy-MM-dd');

      await leaveService.create({
        emp_id: selectedEmployee.id,
        project_code: selectedProject,
        month: monthDate,
        leave_count: parseInt(newLeaveCount),
      });

      setSuccess('Leave added successfully');
      setAddDialogOpen(false);
      loadLeaves();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to add leave');
    }
  };

  const handleDeleteClick = (leave) => {
    setLeaveToDelete(leave);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!leaveToDelete) return;
    try {
      await leaveService.delete(leaveToDelete.leave_id);
      setSuccess('Leave deleted successfully');
      loadLeaves();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete leave');
      setTimeout(() => setError(''), 3000);
    } finally {
      setDeleteDialogOpen(false);
      setLeaveToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setLeaveToDelete(null);
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!filteredLeaves || filteredLeaves.length === 0) return;

    const headers = ['SNo', 'Employee ID', 'Employee Name', 'Project Code', 'Location', 'Month', 'Leave Count'];

    const dataRows = filteredLeaves.map((leave, index) => [
      index + 1,
      leave.emp_id || 'N/A',
      leave.employee_name || 'N/A',
      leave.project_code || 'N/A',
      leave.location_name || 'N/A',
      format(new Date(leave.month), 'MMM yyyy'),
      `${leave.leave_count} days`,
    ]);

    const wsData = [headers, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    headers.forEach((_, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
      let bgColor = '00897B';
      if (index === 6) bgColor = 'E65100';

      if (ws[cellRef]) {
        ws[cellRef].s = {
          fill: { fgColor: { rgb: bgColor } },
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
          alignment: { horizontal: 'center', vertical: 'center' },
          border: { bottom: { style: 'medium', color: { rgb: '000000' } } },
        };
      }
    });

    dataRows.forEach((row, rowIndex) => {
      row.forEach((_, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex });
        if (ws[cellRef]) {
          ws[cellRef].s = {
            fill: { fgColor: { rgb: rowIndex % 2 === 0 ? 'FFFFFF' : 'E0F2F1' } },
            alignment: { horizontal: 'left', vertical: 'center' },
          };
        }
      });
    });

    ws['!cols'] = [{ wch: 6 }, { wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 15 }, { wch: 12 }, { wch: 12 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leaves');
    XLSX.writeFile(wb, `Leaves_${selectedProject}_${selectedMonth}.xlsx`);
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!filteredLeaves || filteredLeaves.length === 0) return;

    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.setTextColor(0, 137, 123);
    doc.text('Leave Management', 14, 20);

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Project: ${selectedProject} | Month: ${selectedMonth ? format(new Date(selectedMonth + '-01'), 'MMMM yyyy') : ''}`, 14, 28);

    const headers = [['SNo', 'Employee ID', 'Employee Name', 'Project Code', 'Location', 'Month', 'Leave Count']];

    const data = filteredLeaves.map((leave, index) => [
      index + 1,
      leave.emp_id || 'N/A',
      leave.employee_name || 'N/A',
      leave.project_code || 'N/A',
      leave.location_name || 'N/A',
      format(new Date(leave.month), 'MMM yyyy'),
      `${leave.leave_count} days`,
    ]);

    autoTable(doc, {
      head: headers,
      body: data,
      startY: 35,
      theme: 'grid',
      headStyles: {
        fillColor: [0, 137, 123],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        6: { halign: 'center', fillColor: [255, 243, 224] },
      },
      alternateRowStyles: { fillColor: [224, 242, 241] },
    });

    doc.save(`Leaves_${selectedProject}_${selectedMonth}.pdf`);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 2, mb: 4 }}>
        {/* Page Header */}
        <Box sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #00897B 0%, #26A69A 100%)',
          color: 'white',
          boxShadow: '0 4px 15px rgba(0, 137, 123, 0.3)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <EventBusyIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">Leave Management</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Track and manage employee leaves by project and month
              </Typography>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={5}>
              <TextField
                select
                fullWidth
                label="Project Code"
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                helperText="Select a project to view leaves"
              >
                <MenuItem value="">-- Select Project --</MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.project_code} value={project.project_code}>
                    {project.project_code} - {project.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Month"
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Select a month to view leaves"
              />
            </Grid>
            <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Export to PDF">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={exportToPDF}
                  disabled={filteredLeaves.length === 0}
                >
                  PDF
                </Button>
              </Tooltip>
              <Tooltip title="Export to Excel">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TableChartIcon />}
                  onClick={exportToExcel}
                  disabled={filteredLeaves.length === 0}
                >
                  Excel
                </Button>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddEmployee}
                disabled={!selectedProject || !selectedMonth}
                fullWidth
              >
                Add Leave
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Leave Records Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellStyle, width: 60 }} align="center">SNo</TableCell>
                  <TableCell sx={headerCellStyle}>Employee ID</TableCell>
                  <TableCell sx={headerCellStyle}>Employee Name</TableCell>
                  <TableCell sx={headerCellStyle}>Project Code</TableCell>
                  <TableCell sx={headerCellStyle}>Location</TableCell>
                  <TableCell sx={headerCellStyle}>Month</TableCell>
                  <TableCell sx={{ ...headerCellStyle, backgroundColor: '#E65100', borderBottom: '3px solid #BF360C' }}>Leave Count</TableCell>
                  <TableCell align="center" sx={headerCellStyle}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!selectedProject || !selectedMonth ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      Please select a project and month to view leave records
                    </TableCell>
                  </TableRow>
                ) : filteredLeaves.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      No leave records found for the selected project and month
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeaves.map((leave, index) => (
                    <TableRow
                      key={leave.leave_id}
                      sx={{
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#E0F2F1',
                        '&:hover': { backgroundColor: '#B2DFDB' },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell><strong>{leave.emp_id || 'N/A'}</strong></TableCell>
                      <TableCell>{leave.employee_name || 'N/A'}</TableCell>
                      <TableCell>{leave.project_code || 'N/A'}</TableCell>
                      <TableCell>{leave.location_name || 'N/A'}</TableCell>
                      <TableCell>
                        {format(new Date(leave.month), 'MMM yyyy')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#E65100' }}>{leave.leave_count} days</TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          onClick={() => handleEdit(leave)}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(leave)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Update Leave Count</DialogTitle>
          <DialogContent>
            {editError && (
              <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
                {editError}
              </Alert>
            )}
            {editingLeave && (
              <Box sx={{ pt: editError ? 0 : 2 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Employee:</strong> {editingLeave.employee_name} ({editingLeave.emp_id})
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Project:</strong> {editingLeave.project_code}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Month:</strong> {format(new Date(editingLeave.month), 'MMMM yyyy')}
                </Typography>
                <TextField
                  fullWidth
                  label="Leave Count"
                  type="number"
                  value={editLeaveCount}
                  onChange={(e) => setEditLeaveCount(e.target.value)}
                  margin="normal"
                  inputProps={{
                    min: 0,
                    step: 1,
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEditSave} variant="contained" color="primary">
              Update
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Leave Dialog */}
        <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Leave</DialogTitle>
          <DialogContent>
            {addError && (
              <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
                {addError}
              </Alert>
            )}
            <Box sx={{ pt: addError ? 0 : 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Project:</strong> {selectedProject}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Month:</strong> {selectedMonth ? format(new Date(selectedMonth + '-01'), 'MMMM yyyy') : ''}
              </Typography>
              <Autocomplete
                options={employees}
                getOptionLabel={(option) => `${option.emp_id} - ${option.name}`}
                value={selectedEmployee}
                onChange={(event, newValue) => setSelectedEmployee(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employee"
                    required
                    margin="normal"
                    helperText="Select employee to add leave"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
              />
              <TextField
                fullWidth
                label="Leave Count"
                type="number"
                value={newLeaveCount}
                onChange={(e) => setNewLeaveCount(e.target.value)}
                margin="normal"
                inputProps={{
                  min: 0,
                  step: 1,
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddSave} variant="contained" color="primary">
              Add
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Leave Record"
          message="Are you sure you want to delete this leave record?"
          details={leaveToDelete ? [
            { label: 'Employee', value: leaveToDelete.employee_name },
            { label: 'Month', value: format(new Date(leaveToDelete.month), 'MMMM yyyy') },
            { label: 'Leave Days', value: `${leaveToDelete.leave_count} days` },
          ] : []}
        />
      </Box>
    </Container>
  );
};

export default LeaveList;
